import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevisForm({ prefilledService = "", prefilledVille = "", prefilledTypeClient = "", prefilledMessage = "", source = "direct", className = "" }) {
  const [services, setServices] = useState([]);
  const [villes, setVilles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nom: "", telephone: "", email: "",
    service: prefilledService, ville: prefilledVille,
    type_client: prefilledTypeClient || "particulier",
    message: prefilledMessage, surface: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Promise.all([axios.get(`${API}/services`), axios.get(`${API}/villes`)]).then(([s, v]) => {
      setServices(s.data);
      setVilles(v.data);
    });
  }, []);

  useEffect(() => {
    setForm(f => ({
      ...f,
      ...(prefilledService && { service: prefilledService }),
      ...(prefilledVille && { ville: prefilledVille }),
      ...(prefilledTypeClient && { type_client: prefilledTypeClient }),
      ...(prefilledMessage && { message: prefilledMessage }),
    }));
  }, [prefilledService, prefilledVille, prefilledTypeClient, prefilledMessage]);

  const validate = () => {
    const errs = {};
    if (!form.nom.trim()) errs.nom = "Requis";
    if (!form.telephone.trim()) errs.telephone = "Requis";
    else if (!/^(\+41|0)\d{9,}/.test(form.telephone.replace(/\s/g, ""))) errs.telephone = "Format suisse invalide";
    if (!form.email.trim()) errs.email = "Requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Format invalide";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        nom: form.nom, telephone: form.telephone, email: form.email,
        service: form.service, ville: form.ville, type_client: form.type_client,
        message: form.surface ? `Surface/quantité: ${form.surface}. ${form.message}` : form.message,
        source, page_origine: window.location.pathname,
        website: "",
      });
      setSubmitted(true);
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`bg-white border border-[#10B981]/30 rounded-lg p-8 text-center ${className}`} data-testid="devis-form-success">
        <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#062133]">Merci !</h3>
        <p className="text-[#062133]/60 mt-2">Nous vous recontactons dans les 2 heures.</p>
      </div>
    );
  }

  const u = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const fieldClass = (name) => errors[name] ? "border-red-400 focus:ring-red-400" : "focus:ring-[#336DBF]";

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`} data-testid="devis-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Nom *</label>
          <Input value={form.nom} onChange={e => u("nom", e.target.value)} className={fieldClass("nom")} data-testid="devis-nom" />
          {errors.nom && <p className="text-xs text-red-500 mt-1">{errors.nom}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Téléphone *</label>
          <Input value={form.telephone} onChange={e => u("telephone", e.target.value)} placeholder="+41 79 000 00 00" className={fieldClass("telephone")} data-testid="devis-telephone" />
          {errors.telephone && <p className="text-xs text-red-500 mt-1">{errors.telephone}</p>}
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-[#062133] mb-1.5 block">Email *</label>
        <Input type="email" value={form.email} onChange={e => u("email", e.target.value)} className={fieldClass("email")} data-testid="devis-email" />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Service</label>
          <Select value={form.service} onValueChange={v => u("service", v)}>
            <SelectTrigger data-testid="devis-service"><SelectValue placeholder="Choisir un service" /></SelectTrigger>
            <SelectContent>{services.map(s => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Ville</label>
          <Select value={form.ville} onValueChange={v => u("ville", v)}>
            <SelectTrigger data-testid="devis-ville"><SelectValue placeholder="Choisir une ville" /></SelectTrigger>
            <SelectContent>{villes.map(v => <SelectItem key={v.slug} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Type client</label>
          <Select value={form.type_client} onValueChange={v => u("type_client", v)}>
            <SelectTrigger data-testid="devis-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="particulier">Particulier</SelectItem>
              <SelectItem value="entreprise">Entreprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Surface / Quantité</label>
          <Input value={form.surface} onChange={e => u("surface", e.target.value)} placeholder="Ex: 50 m2, 3 canapés..." data-testid="devis-surface" />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-[#062133] mb-1.5 block">Message</label>
        <Textarea value={form.message} onChange={e => u("message", e.target.value)} rows={3} data-testid="devis-message" />
      </div>
      <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors disabled:opacity-50 text-base" data-testid="devis-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        Demander mon devis gratuit
      </button>
    </form>
  );
}
